import { Field, FieldProps } from 'formik';
import React from 'react';

import { ColorData, FieldColor } from '../../../common/fields/FieldColor';
import { StaticLabel } from '../../../common/labels';
import WysiwygPopUpShortCodesContent from '../../../common/wysiwyg/WysiwygPopUpShortCodesContent';
import { useTemplateUI } from '../useTemplateUI';
import { errorsToString, isPlaceholder, defaultColorFallback } from './helper';
import {
  PkpassInputSettingsPopUp,
  usePkpassInputSettingsPopup,
} from './PkpassInputSettingsPopUp';
import { colorValueSchema } from '../../../../utils/validation/helper';
import IconButton from '@material-ui/core/IconButton';
import { SettingsIcon } from '../../../common/icons';

type ValueType =
  | {
      type: 'string';
      value?: string;
      fallback?: string;
    }
  | undefined;

interface ColorWithFallbackInputProps<T> extends ColorWithFallbackProps {
  field: FieldProps<T>;
}

const ColorWithFallbackInput = (
  props: ColorWithFallbackInputProps<ValueType>
): JSX.Element => {
  const { field, label, helpText, name, required } = props;

  const value = field.field.value ? field.field.value.value : undefined;
  const object = field.field.value;
  const hasFallback = object && isPlaceholder('string', object);
  const fallback = field.field.value ? field.field.value.fallback : undefined;
  let localError;
  try {
    colorValueSchema.validateSync(object);
  } catch (e) {
    localError = e.message;
  }
  const error = errorsToString(field.form.errors, [name]) || localError;

  const getFallback = (v: string | undefined) => {
    const h = isPlaceholder('string', { value: v, type: 'string' });
    if (h) {
      return fallback || defaultColorFallback;
    }
    return fallback;
  };

  const templateUI = useTemplateUI();
  const change = (color: string | undefined) => {
    let newValue;
    if (color !== '') {
      newValue = {
        value: color,
        fallback: getFallback(color),
        type: 'string',
      };
    }
    field.form.setFieldValue(name, newValue);
  };

  const handleOnBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    change(event.target.value);
    field.field.onBlur(event);
  };

  const handleChange = (event: unknown, d: ColorData) => {
    change(d.value);
  };

  const handleChangeFallback = (event: unknown, data: ColorData) => {
    let newValue;
    if (data.value !== '') {
      newValue = {
        fallback: data.value,
        type: 'string',
        value: value,
      };
    } else {
      newValue = {
        fallback: undefined,
        type: 'string',
        value: value,
      };
    }
    field.form.setFieldValue(name, newValue);
  };

  const handleSelectShortCode = (shortCode: string) => {
    field.form.setFieldValue(name, {
      fallback: getFallback(shortCode),
      type: 'string',
      value: shortCode,
    });
  };
  const { anchorEl, handleClose, handleOpen } = usePkpassInputSettingsPopup();
  return (
    <>
      <FieldColor
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={handleOnBlur}
        label={label}
        helpText={helpText}
        error={error}
        required={required}
        endAdornment={
          <IconButton
            onClick={handleOpen}
            disabled={!hasFallback && templateUI.colorShortCodes.length === 0}
          >
            <SettingsIcon style={{ height: '0.7em', width: '0.7em' }} />
          </IconButton>
        }
      />
      <PkpassInputSettingsPopUp onClose={handleClose} anchorEl={anchorEl}>
        <FieldColor
          disabled={!hasFallback}
          label={<span>{StaticLabel.Fallback}</span>}
          name={name}
          value={fallback}
          onChange={handleChangeFallback}
        />
        {templateUI.colorShortCodes.length > 0 && (
          <WysiwygPopUpShortCodesContent
            shortCodes={templateUI.colorShortCodes}
            onSelect={handleSelectShortCode}
          />
        )}
      </PkpassInputSettingsPopUp>
    </>
  );
};

export interface ColorWithFallbackProps {
  name: string;
  label: JSX.Element;
  helpText: JSX.Element;
  required?: boolean;
}

export const PkpassColor = (props: ColorWithFallbackProps): JSX.Element => {
  return (
    <Field name={props.name}>
      {(field: FieldProps) => (
        <ColorWithFallbackInput {...props} field={field} />
      )}
    </Field>
  );
};
